import React, { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { attribution, track } from "@/lib/analytics";

const Q1 = [
  { v: "per_me", l: "Per me" },
  { v: "per_genitore", l: "Per un genitore" },
  { v: "per_familiare", l: "Per un familiare" },
  { v: "per_cliente", l: "Per un cliente / paziente" },
];
const Q2 = [
  { v: "difficolta_camminare", l: "Difficoltà nel camminare" },
  { v: "non_usa_auto", l: "Non utilizza più l'automobile" },
  { v: "maggiore_autonomia", l: "Vuole maggiore autonomia" },
  { v: "protezione_meteo", l: "Cerca protezione da pioggia/freddo" },
  { v: "altro", l: "Altro" },
];

const Choice = ({ selected, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`w-full text-left px-5 py-4 rounded-xl border-2 text-lg font-medium transition-all ${selected ? "border-emerald-700 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white hover:border-emerald-300 text-slate-700"}`}>
    {children}
  </button>
);

export const QualificationQuiz = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ who: "", need: "", province: "", first_name: "", phone: "", email: "", privacy_consent: false });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const total = 4;

  const next = () => { if (step === 0) track("lead_form_start", { form: "qualification" }); setStep((s) => Math.min(s + 1, total)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!data.first_name || !data.phone) return toast.error("Inserisci nome e telefono.");
    if (!data.privacy_consent) return toast.error("È necessario accettare la privacy policy.");
    setLoading(true);
    try {
      await api.post("/leads", {
        first_name: data.first_name, phone: data.phone, email: data.email, province: data.province,
        lead_type: data.who, requested_action: "qualified", notes: `Esigenza: ${data.need}`,
        privacy_consent: data.privacy_consent, ...attribution(),
      });
      track("qualified_lead", { form: "qualification", who: data.who });
      track("lead_form_submit", { form: "qualification" });
      setSent(true);
    } catch { toast.error("Errore nell'invio. Riprova."); }
    finally { setLoading(false); }
  };

  if (sent) return (
    <div className="rounded-3xl bg-emerald-50 border border-emerald-200 p-10 text-center" data-testid="quiz-success">
      <CheckCircle2 className="mx-auto text-emerald-700 mb-3" size={56} />
      <h3 className="text-2xl font-bold text-emerald-900" style={{ fontFamily: "Outfit" }}>Grazie {data.first_name}!</h3>
      <p className="text-emerald-800/80 mt-2 text-lg">Abbiamo ricevuto la tua richiesta. Un consulente ti contatterà per capire se SENIORCAR fa al caso tuo.</p>
    </div>
  );

  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-md p-6 sm:p-8" data-testid="qualification-quiz">
      <Progress value={(step / total) * 100} className="h-2 mb-6" />
      {step === 0 && (
        <div className="sc-fade-up">
          <h3 className="text-2xl font-bold text-slate-900 mb-5" style={{ fontFamily: "Outfit" }}>Per chi stai cercando SENIORCAR?</h3>
          <div className="grid gap-3">
            {Q1.map((o) => <Choice key={o.v} testid={`quiz-who-${o.v}`} selected={data.who === o.v} onClick={() => { setData({ ...data, who: o.v }); next(); }}>{o.l}</Choice>)}
          </div>
        </div>
      )}
      {step === 1 && (
        <div className="sc-fade-up">
          <h3 className="text-2xl font-bold text-slate-900 mb-5" style={{ fontFamily: "Outfit" }}>Qual è l'esigenza principale?</h3>
          <div className="grid gap-3">
            {Q2.map((o) => <Choice key={o.v} testid={`quiz-need-${o.v}`} selected={data.need === o.v} onClick={() => { setData({ ...data, need: o.v }); next(); }}>{o.l}</Choice>)}
          </div>
          <Button variant="ghost" onClick={back} className="mt-5 gap-2" data-testid="quiz-back-1"><ArrowLeft size={18} /> Indietro</Button>
        </div>
      )}
      {step === 2 && (
        <div className="sc-fade-up">
          <h3 className="text-2xl font-bold text-slate-900 mb-5" style={{ fontFamily: "Outfit" }}>In quale provincia?</h3>
          <Input placeholder="Es. MI, RM, TO..." value={data.province} onChange={(e) => setData({ ...data, province: e.target.value })} className="h-14 text-lg" data-testid="quiz-province" />
          <div className="flex justify-between mt-6">
            <Button variant="ghost" onClick={back} className="gap-2" data-testid="quiz-back-2"><ArrowLeft size={18} /> Indietro</Button>
            <Button onClick={next} className="gap-2 text-white font-bold px-6 h-12 rounded-full" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="quiz-next-2">Continua <ArrowRight size={18} /></Button>
          </div>
        </div>
      )}
      {step >= 3 && (
        <div className="sc-fade-up">
          <h3 className="text-2xl font-bold text-slate-900 mb-5" style={{ fontFamily: "Outfit" }}>Come possiamo ricontattarti?</h3>
          <div className="space-y-3">
            <Input placeholder="Nome" value={data.first_name} onChange={(e) => setData({ ...data, first_name: e.target.value })} className="h-14 text-lg" data-testid="quiz-name" />
            <Input placeholder="Telefono" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} className="h-14 text-lg" data-testid="quiz-phone" />
            <Input placeholder="Email (facoltativa)" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className="h-14 text-lg" data-testid="quiz-email" />
            <label className="flex items-start gap-2 text-sm text-slate-600 pt-1">
              <Checkbox checked={data.privacy_consent} onCheckedChange={(v) => setData({ ...data, privacy_consent: !!v })} className="mt-0.5" data-testid="quiz-privacy" />
              <span>Ho letto e accetto la <a href="/privacy" className="underline">Privacy Policy</a>.</span>
            </label>
          </div>
          <div className="flex justify-between mt-6">
            <Button variant="ghost" onClick={back} className="gap-2" data-testid="quiz-back-3"><ArrowLeft size={18} /> Indietro</Button>
            <Button onClick={submit} disabled={loading} className="gap-2 text-white font-bold px-6 h-12 rounded-full" style={{ backgroundColor: "var(--sc-amber)" }} data-testid="quiz-submit">
              {loading ? "Invio..." : "RICHIEDI INFORMAZIONI"} <ArrowRight size={18} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
